import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  IncidentSchema,
  IncidentCreateInputSchema,
  IncidentUpdateInputSchema,
  type Incident,
  type IncidentCreateInput,
  type IncidentUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `incident` (événement dommageable / sinistre, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`IncidentCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide.
 */

export type { IncidentCreateInput };

/** Nom de la collection Firestore de la famille incident. */
export const INCIDENTS_COLLECTION = 'incidents';

/**
 * Repository typé pour la famille `incident`.
 * Toute lecture est validée par `IncidentSchema`, toute écriture par
 * `IncidentCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface IncidentRepository {
  create(input: IncidentCreateInput): Promise<Incident>;
  update(id: string, patch: IncidentUpdateInput): Promise<Incident>;
  list(options?: ListOptions): Promise<Incident[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Incident | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Incident>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Incident>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Incident>;
}

const incidentConverter = createConverter<Incident>((raw: DocumentData) => IncidentSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createIncidentRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): IncidentRepository {
  const col = collection(db, INCIDENTS_COLLECTION).withConverter(incidentConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Incident>(db, col, INCIDENTS_COLLECTION, getActorId);

  return {
    async create(input: IncidentCreateInput): Promise<Incident> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = IncidentCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.occurredAt !== undefined) optional['occurredAt'] = fields.occurredAt;
      if (fields.severity !== undefined) optional['severity'] = fields.severity;
      if (fields.involvedRefs !== undefined) optional['involvedRefs'] = fields.involvedRefs;
      if (fields.description !== undefined) optional['description'] = fields.description;
      if (fields.actionsTaken !== undefined) optional['actionsTaken'] = fields.actionsTaken;
      if (fields.closedAt !== undefined) optional['closedAt'] = fields.closedAt;
      if (fields.custodyId !== undefined) optional['custodyId'] = fields.custodyId;
      if (fields.inspectionId !== undefined) optional['inspectionId'] = fields.inspectionId;
      if (fields.ticketId !== undefined) optional['ticketId'] = fields.ticketId;
      if (fields.followUpTaskIds !== undefined) optional['followUpTaskIds'] = fields.followUpTaskIds;
      if (fields.documentIds !== undefined) optional['documentIds'] = fields.documentIds;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Incident);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return IncidentSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: IncidentUpdateInput): Promise<Incident> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = IncidentUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, INCIDENTS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec IncidentSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`incident ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Incident[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Incident | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
